import { ReactElement, useEffect, useState } from 'react';
import { NumberFormatBase } from 'react-number-format';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import endpoints from '../../../config/endpoints';
import { setLoading } from '../../../services/reducers/homeSlice';
import restApi from '../../../services/restApi';
import tools from '../../../utils/tools';
import { Sidebar } from '../../Layout/Sidebar';

const ProductManage = () => {
  const dispatch = useDispatch();
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [attributeGroups, setAttributeGroups] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [cars, setCars] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [shelves, setShelves] = useState<any[]>([]);
  const [attributeGroup, setAttributeGroup] = useState<any>();
  const [selectedAttributes, setSelectedAttributes] = useState<any>({});
  const [form, setForm] = useState<any>();

  const send = async () => {
    // Validation
    if (!form.titleEng?.trim()) {
      Swal.fire({
        title: 'خطا',
        text: 'لطفا نام انگلیسی محصول را وارد کنید',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
      return;
    }

    if (!form.categoryId) {
      Swal.fire({
        title: 'خطا',
        text: 'لطفا دسته بندی را انتخاب کنید',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
      return;
    }

    if (!form.sku?.trim()) {
      Swal.fire({
        title: 'خطا',
        text: 'لطفا شماره فنی (SKU) محصول را وارد کنید',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
      return;
    }

    dispatch(setLoading(true));

    try {
      // ✅ Clean the form data before sending - convert empty strings to null/0
      const cleanedForm = {
        titleEng: form.titleEng?.trim(),
        shortText: form.shortText || '',
        longText: form.longText || '',
        categoryId: form.categoryId,
        attributeGroupId: form.attributeGroupId || null,
        tagId: form.tagId || null,
        model: form.model || '',
        brandId: form.brandId || null,
        sku: form.sku || '',
        status: form.status !== undefined ? form.status : 1,
        sort: form.sort || '',
        accountingCode: form.accountingCode || '',
        cars: form.cars || [],
        // Convert numeric fields properly - empty string becomes null or 0
        price: form.price && form.price !== '' ? parseFloat(form.price) : 0,
        wholesomePrice: form.wholesomePrice && form.wholesomePrice !== '' ? parseFloat(form.wholesomePrice) : null,
        discountPrice: form.discountPrice && form.discountPrice !== '' ? parseFloat(form.discountPrice) : null,
        itemsPerBox: form.itemsPerBox && form.itemsPerBox !== '' ? parseInt(form.itemsPerBox) : null,
        itemsPerRoll: form.itemsPerRoll && form.itemsPerRoll !== '' ? parseInt(form.itemsPerRoll) : null,
        // Day-to-day stock (receiving new boxes/rolls) still belongs on the
        // Inventory page — these two fields are for correcting the recorded
        // count directly (e.g. reconciling after a physical count). The
        // backend still logs the difference to Cardex as a "correction".
        boxCount: form.boxCount === '' || form.boxCount === undefined ? undefined : parseInt(form.boxCount),
        rollCount: form.rollCount === '' || form.rollCount === undefined ? undefined : parseInt(form.rollCount),
      };

      console.log('Sending product data:', cleanedForm);

      const res = await restApi(
        process.env.REACT_APP_BASE_URL + '/admin/product/basic/' + (paramId || ''),
        true
      ).post(cleanedForm);

      console.log('API Response:', res);

      // Check for error responses
      if (res.code !== 200 && res.code !== 201) {
        let errorMessage = 'خطا در ذخیره محصول';

        // Handle different error codes
        if (res.code === 409) {
          const message = res?.message || res?.data || '';
          if (typeof message === 'string') {
            if (message.toLowerCase().includes('sku')) {
              errorMessage = 'SKU تکراری است. لطفا SKU دیگری انتخاب کنید';
            } else if (message.toLowerCase().includes('slug')) {
              errorMessage = 'نام محصول تکراری است. لطفا نام دیگری انتخاب کنید';
            } else {
              errorMessage = message;
            }
          }
        } else if (res.code === 400) {
          errorMessage = res?.data || 'اطلاعات وارد شده نامعتبر است';
        } else if (res.code === 1002) {
          errorMessage = 'شناسه محصول نامعتبر است';
        } else {
          errorMessage = res?.data || res?.message || 'خطا در ذخیره محصول';
        }

        throw new Error(errorMessage);
      }

      // Get the product ID from response or use paramId for updates
      const id = res?.data?.id || paramId;

      if (!id) {
        throw new Error('شناسه محصول در پاسخ سرور یافت نشد');
      }

      // Prepare media upload
      let formData = new FormData();

      // Add new images
      images?.forEach((image: any) => {
        if (image.data) {
          formData.append(`files[${image.id}]`, image.data);
        }
      });

      // Add deleted image IDs
      images?.filter(e => e.id?.includes('d-')).forEach(e => {
        formData.append('deleted[]', e.id.split('-')[1]);
      });

      // Prepare parallel requests
      const requests = [
        restApi(process.env.REACT_APP_BASE_URL + '/admin/product/attributes/' + id, true)
          .post({ attributes: selectedAttributes }),
        restApi(process.env.REACT_APP_BASE_URL + '/admin/product/medias/' + id, true)
          .upload(formData),
      ];

      // Add shelf products request if applicable
      if (form.shelfProducts && form.shelfProducts.length > 0) {
        requests.push(
          restApi(process.env.REACT_APP_BASE_URL + '/admin/product/shelf-products/' + id, true)
            .put({
              shelfProducts: form.shelfProducts.map(sp => ({
                shelfId: sp.shelfId,
                count: sp.count || 0,
                unitType: sp.unitType || 'box'
              }))
            })
        );
      }

      // Execute all requests in parallel
      await Promise.all(requests);

      // Success notification
      Swal.fire({
        title: 'موفق',
        text: `محصول با موفقیت ${paramId ? 'ویرایش' : 'ساخته'} شد`,
        icon: 'success',
        confirmButtonText: 'متوجه شدم',
        didClose() {
          navigate('/product');
        }
      });

    } catch (error: any) {
      console.error('Error saving product:', error);

      // Show user-friendly error message
      Swal.fire({
        title: 'ناموفق',
        text: error.message || 'خطا در ذخیره محصول. لطفا دوباره تلاش کنید',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const list = () => {
    const rows: ReactElement[] = [];
    attributeGroup?.attributes?.filter((e: any) => !e.isTechnology)?.forEach((attribute: any, index: number) => {
      rows.push(
        <div className="inputAttribute" key={`modal-${index}`}>
          <label className="sideBarTitle">{attribute.title}</label>
          <select
            className="modalSelector"
            value={selectedAttributes[attribute.slug] || ''}
            onChange={(input) => setSelectedAttributes(prev => ({
              ...prev,
              [attribute.slug]: input.target.value
            }))}
          >
            <option value="">انتخاب کنید</option>
            {attribute.attributeValues?.map((attributeValue: any, valueIndex: number) => (
              <option key={valueIndex} value={attributeValue.slug}>
                {attributeValue.title}
              </option>
            ))}
          </select>
        </div>
      );
    });

    return rows;
  };

  // Improved Shelf Products Management List
  const shelfProductsList = () => {
    const rows: ReactElement[] = [];

    if (!form?.shelfProducts || form.shelfProducts.length === 0) {
      return (
        <tr>
          <td colSpan={5} className="empty-shelf-state">
            <p>هنوز هیچ قفسه‌ای اضافه نشده است</p>
            <p>برای شروع، روی دکمه "اضافه کردن قفسه" کلیک کنید</p>
          </td>
        </tr>
      );
    }

    form.shelfProducts.forEach((shelfProduct: any, index: number) => {
      const shelfObj = shelves.find(s => s.id == shelfProduct.shelfId);

      rows.push(
        <tr key={index} className="shelf-row">
          {/* Delete Button */}
          <td className="shelf-cell">
            <button
              type="button"
              className="delete-button"
              onClick={() => {
                setForm(prev => ({
                  ...prev,
                  shelfProducts: prev.shelfProducts.filter((_, i) => i !== index)
                }));
              }}
              title="حذف"
            >
              ×
            </button>
          </td>

          {/* Count */}
          <td className="shelf-cell">
            <div className="count-input-container">
              <button
                type="button"
                className="count-button"
                onClick={() => {
                  setForm(prev => {
                    const cp = { ...prev };
                    if (cp.shelfProducts[index].count > 0) {
                      cp.shelfProducts[index].count = Number(cp.shelfProducts[index].count) - 1;
                    }
                    return cp;
                  });
                }}
              >
                −
              </button>
              <input
                type="number"
                className="count-input"
                value={shelfProduct.count}
                min="0"
                onChange={(e) => {
                  setForm(prev => {
                    const cp = { ...prev };
                    cp.shelfProducts[index].count = Number(e.target.value);
                    return cp;
                  });
                }}
              />
              <button
                type="button"
                className="count-button"
                onClick={() => {
                  setForm(prev => {
                    const cp = { ...prev };
                    cp.shelfProducts[index].count = Number(cp.shelfProducts[index].count) + 1;
                    return cp;
                  });
                }}
              >
                +
              </button>
            </div>
          </td>

          {/* Unit Type */}
          <td className="shelf-cell">
            <select
              className="unit-type-select"
              value={shelfProduct.unitType}
              onChange={(e) => {
                setForm(prev => {
                  const cp = { ...prev };
                  cp.shelfProducts[index].unitType = e.target.value;
                  return cp;
                });
              }}
            >
              <option value="box">قوطی</option>
              <option value="roll">رولی</option>
            </select>
          </td>

          {/* Shelf Selection */}
          <td className="shelf-cell">
            <div className="shelf-select-container">
              <Select
                className="shelf-select"
                options={shelves.map(s => ({ value: s.id, label: s.title }))}
                value={{ value: shelfProduct.shelfId, label: shelfObj?.title || '' }}
                onChange={(selected) => {
                  setForm(prev => {
                    const cp = { ...prev };
                    cp.shelfProducts[index].shelfId = selected ? selected.value : null;
                    return cp;
                  });
                }}
                placeholder="جستجو قفسه..."
                isSearchable={true}
                isClearable={true}
                noOptionsMessage={() => "قفسه‌ای یافت نشد"}
                loadingMessage={() => "در حال جستجو..."}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, minHeight: '36px' }),
                  menu: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
          </td>

          {/* Row Number */}
          <td className="shelf-cell">
            <div className="row-number">
              {index + 1}
            </div>
          </td>
        </tr>
      );
    });

    return rows;
  };

  const handleFileChange = (e: any) => {
    const files: any[] = [];

    for (let i = 0; i < e.target.files.length; i++) {
      files.push({
        preview: URL.createObjectURL(e.target.files[i]),
        data: e.target.files[i],
        id: 'n-' + (images.length + i)
      });
    }

    setImages([...images, ...files]);
  };

  const fetchData = async () => {
    dispatch(setLoading(true));

    try {
      // Base requests that always run
      const baseRequests = [
        restApi(process.env.REACT_APP_BASE_URL + '/admin/attributeGroup', true).get(),
        restApi(process.env.REACT_APP_BASE_URL + '/admin/brand', true).get(),
        restApi(process.env.REACT_APP_BASE_URL + '/admin/tag', true).get(),
        restApi(process.env.REACT_APP_BASE_URL + '/admin/category', true).get(),
        restApi(endpoints.car.index, true).get(),
        restApi(process.env.REACT_APP_BASE_URL + '/admin/shelf/dropdown', true).get(),
      ];

      // Add conditional requests for existing products
      if (paramId) {
        baseRequests.push(
          restApi(process.env.REACT_APP_BASE_URL + '/admin/product/single/' + paramId, true).get()
        );
      }

      const results = await Promise.all(baseRequests);

      // Set basic data
      setAttributeGroups(results[0]?.data || []);
      setBrands(results[1]?.data || []);
      setTags(results[2]?.data || []);
      setCategories(results[3]?.data || []);
      setCars(results[4]?.data || []);
      setShelves(results[5]?.data || []);

      if (paramId) {
        // When editing existing product
        const productData = results[6]?.data;

        if (productData) {
          setForm({
            titleEng: productData.title || '',
            shortText: productData.shortText || '',
            longText: productData.longText || '',
            categoryId: productData.categoryId || '',
            attributeGroupId: productData.attributeGroupId || '',
            cars: productData.cars?.map((e: any) => e.id.toString()) || [],
            tagId: productData.tags?.length > 0 ? productData.tags[0]?.id : null,
            model: productData.model || '',
            brandId: productData.brandId || '',
            status: productData.status || 1,
            sku: productData.sku || '',
            count: productData.count || 0,
            price: productData.price || '',
            wholesomePrice: productData.wholesomePrice || '',
            discountPrice: productData.discountPrice || '',
            sort: productData.sort || '',
            slug: productData.slug || '',
            itemsPerBox: productData.itemsPerBox || '',
            itemsPerRoll: productData.itemsPerRoll || '',
            boxCount: productData.boxCount ?? '',
            rollCount: productData.rollCount ?? '',
            accountingCode: productData.accountingCode || '',
            shelfProducts: productData.shelfProducts || [],
          });

          setImages(productData.medias?.map((e: any, index: number) => ({
            preview: e.url,
            id: e.id?.toString()
          })) || []);

          const attributes: any = {};
          productData.attributeProducts?.filter((e: any) => !e.attribute?.isTechnology).forEach((e: any) => {
            attributes[e.attributeSlug] = e.attributeValueSlug;
          });
          setSelectedAttributes(attributes);
        }
      } else {
        // When creating new product
        setForm({
          titleEng: '',
          shortText: '',
          longText: '',
          categoryId: '',
          attributeGroupId: '',
          cars: [],
          tagId: null,
          model: '',
          brandId: '',
          status: 1,
          sku: '',
          count: 0,
          price: '',
          wholesomePrice: '',
          discountPrice: '',
          sort: '',
          itemsPerBox: '',
          itemsPerRoll: '',
          boxCount: '',
          rollCount: '',
          accountingCode: '',
          shelfProducts: [],
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        title: 'خطا',
        text: 'خطا در بارگزاری اطلاعات',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setForm((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const removeImage = (imageId: string) => {
    setImages(prev => {
      const cp = [...prev];
      const index = prev.findIndex(e => e?.id === imageId);
      if (index !== -1) {
        if (cp[index].id.includes('n')) {
          cp.splice(index, 1);
        } else {
          cp[index].id = 'd-' + cp[index].id;
        }
      }
      return cp;
    });
  };

  useEffect(() => {
    if (form?.attributeGroupId) {
      setAttributeGroup(attributeGroups?.find((e: any) => e.id == form.attributeGroupId));
    }
  }, [form?.attributeGroupId, attributeGroups]);

  useEffect(() => {
    fetchData();
  }, []);

  if (!form) {
    return (
      <div className="dashboardBody">
        <Sidebar />
        <main className="dashBoardMain main">
          <div>در حال بارگزاری...</div>
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="dashboardBody">
        <Sidebar />
        <main className="dashBoardMain main">
          <div className="addInfoHeader">
            <div className="buttonContainer keepRight">
              <button className="dashboardHeader keepRight" onClick={send}>
                <p>{paramId ? 'ویرایش' : 'ایجاد'}</p>
              </button>
              {paramId && (
                <span className="saveButton clickable" onClick={() => navigate(`/product/${form?.slug}`)}>
                  <p>مشاهده محصول</p>
                </span>
              )}
            </div>
            <span>
              <h1 className="sideBarTitle">بازگشت به صفحه محصولات</h1>
              <h1 className="dashBoardTitle">{paramId ? 'ویرایش محصول' : 'افزودن محصول جدید'}</h1>
            </span>
            <i className="backAdd clickable" onClick={() => navigate('/product')}></i>
          </div>

          <span className="titles">
            <h3 className="marginRight">توضیحات</h3>
            <h3>اطلاعات محصول</h3>
          </span>

          <section className="addProductTop">
            <div className="AddInfoContainer">
              <label>توضیح کوتاه</label>
              <textarea
                className="ShortDiscription"
                name="shortText"
                value={form?.shortText || ''}
                onChange={(e) => handleInputChange('shortText', e.target.value)}
              />
              <label>توضیح بلند</label>
              <textarea
                className="ShortDiscription"
                name="longText"
                value={form?.longText || ''}
                onChange={(e) => handleInputChange('longText', e.target.value)}
              />
            </div>

            <div className="AddInfoContainer">
              <div className="input2">
                <div className="input3 width100">
                  <label>چینش</label>
                  <input
                    className="halfInput"
                    name="sort"
                    value={form?.sort || ''}
                    onChange={(e) => handleInputChange('sort', e.target.value)}
                  />
                </div>
                <div className="input3 width100">
                  <label>نام انگلیسی</label>
                  <input
                    className="halfInput"
                    name="titleEng"
                    value={form?.titleEng || ''}
                    onChange={(e) => handleInputChange('titleEng', e.target.value)}
                  />
                </div>
              </div>

              <div className="input2">
                <div className="input3 width100">
                  <label>SKU</label>
                  <input
                    className="halfInput"
                    name="sku"
                    value={form?.sku || ''}
                    onChange={(e) => handleInputChange('sku', e.target.value)}
                  />
                </div>
                <div className="input3 width100">
                  <label>مدل</label>
                  <input
                    className="halfInput"
                    name="model"
                    value={form?.model || ''}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                  />
                </div>
                <div className="input3 width100">
                  <label>کد حسابداری</label>
                  <input
                    className="halfInput"
                    name="accountingCode"
                    value={form?.accountingCode || ''}
                    onChange={(e) => handleInputChange('accountingCode', e.target.value)}
                  />
                </div>
              </div>

              <div className="input2">
                <div className="input3 width100">
                  <label>دسته بندی مشخصات فنی</label>
                  <select
                    className="selector"
                    name="attributeGroupId"
                    value={form?.attributeGroupId || ''}
                    onChange={(e) => handleInputChange('attributeGroupId', e.target.value)}
                  >
                    <option value="">انتخاب کنید</option>
                    {attributeGroups?.map((attributeGroup: any, index: number) => (
                      <option key={index} value={attributeGroup.id}>
                        {attributeGroup.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input3 width100">
                  <label>دسته بندی</label>
                  <select
                    className="selector"
                    name="categoryId"
                    value={form?.categoryId || ''}
                    onChange={(e) => handleInputChange('categoryId', e.target.value)}
                  >
                    <option value="">انتخاب کنید</option>
                    {categories?.map((category: any, index: number) => (
                      <option key={index} value={category.id}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input3 width100">
                  <label>برند</label>
                  <select
                    className="selector"
                    name="brandId"
                    value={form?.brandId || ''}
                    onChange={(e) => handleInputChange('brandId', e.target.value)}
                  >
                    <option value="">انتخاب کنید</option>
                    {brands?.map((brand: any, index: number) => (
                      <option key={index} value={brand.id}>
                        {brand.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="input2">
                <div className="input3 width100">
                  <label>تگ</label>
                  <select
                    className="selector"
                    name="tagId"
                    value={form?.tagId || ''}
                    onChange={(e) => handleInputChange('tagId', e.target.value)}
                  >
                    <option value="">انتخاب کنید</option>
                    {tags?.map((tag: any, index: number) => (
                      <option key={index} value={tag.id}>
                        {tag.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input3 width100">
                  <label>ماشین</label>
                  <Select
                    options={cars?.map((car: any) => ({ label: car.title, value: car.id })) || []}
                    value={cars?.filter((e: any) => form?.cars?.includes(e.id.toString())).map((e: any) => ({
                      label: e.title,
                      value: e.id
                    })) || []}
                    isMulti={true}
                    placeholder="انتخاب ماشین ها..."
                    onChange={(selected) => handleInputChange('cars', selected ? selected.map((e: any) => e.value.toString()) : [])}
                  />
                </div>
              </div>
            </div>

            <div className="AddInfoContainer">
              <div className="input3 width100">
                <label>وضعیت</label>
                <select
                  className="selector"
                  name="status"
                  value={form?.status || 1}
                  onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                >
                  <option value={1}>فعال</option>
                  <option value={0}>غیر فعال</option>
                </select>
              </div>

              <div className="input2">
                <div className="input3 width100">
                  <label>قیمت واحد</label>
                  <NumberFormatBase
                    className="halfInput"
                    value={form?.price ?? ''}
                    onValueChange={(values) => handleInputChange('price', values.value)}
                    format={(str) => (str ? tools.formatPrice(Number(str)) : '')}
                  />
                </div>
              </div>

              <div className="unitSection">
                <h5 className="titles">قوطی</h5>
                <div className="input2">
                  <div className="input3 width100">
                    <label>تعداد در کارتن قوطی</label>
                    <input
                      className="halfInput"
                      value={form?.itemsPerBox || ''}
                      onChange={(e) => handleInputChange('itemsPerBox', e.target.value)}
                    />
                  </div>
                  <div className="input3 width100">
                    <label>تعداد</label>
                    <input
                      className="halfInput"
                      type="number"
                      value={form?.boxCount ?? ''}
                      onChange={(e) => handleInputChange('boxCount', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="unitSection">
                <h5 className="titles">رولی</h5>
                <div className="input2">
                  <div className="input3 width100">
                    <label>تعداد در کارتن رولی</label>
                    <input
                      className="halfInput"
                      value={form?.itemsPerRoll || ''}
                      onChange={(e) => handleInputChange('itemsPerRoll', e.target.value)}
                    />
                  </div>
                  <div className="input3 width100">
                    <label>تعداد</label>
                    <input
                      className="halfInput"
                      type="number"
                      value={form?.rollCount ?? ''}
                      onChange={(e) => handleInputChange('rollCount', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="input2">
                <div className="input3 width100">
                  <label>قیمت همکار</label>
                  <NumberFormatBase
                    className="halfInput"
                    value={form?.wholesomePrice ?? ''}
                    onValueChange={(values) => handleInputChange('wholesomePrice', values.value)}
                    format={(str) => (str ? tools.formatPrice(Number(str)) : '')}
                  />
                </div>
              </div>

              <div className="input2">
                <div className="input3 width100">
                  <label>قیمت با تخفیف</label>
                  <NumberFormatBase
                    className="halfInput"
                    value={form?.discountPrice ?? ''}
                    onValueChange={(values) => handleInputChange('discountPrice', values.value)}
                    format={(str) => (str ? tools.formatPrice(Number(str)) : '')}
                  />
                </div>
              </div>
            </div>

            <div className="otherPhotos">
              <div className="mainPhotoHeader">
                <input
                  className="hidden"
                  multiple={true}
                  type="file"
                  title="سایر عکس ها"
                  id="uploadMainPic"
                  onChange={handleFileChange}
                />
                <label className="dashboardHeader keepRight clickable" htmlFor="uploadMainPic">
                  <p>بارگزاری عکس</p>
                  <i className="uploadSvg"></i>
                </label>
                <span className="mainPhotoTitle">
                  <p>گالری</p>
                  <p className="sideBarTitle">حداکثر ۱۶ عکس میتوانید در این قسمت بارگزاری کنید</p>
                </span>
              </div>
              <div className="otherPhotosContainer">
                {images?.filter(e => !e.id?.includes('d'))?.map((image, index) => (
                  <span key={`image-${index}`}>
                    <img src={image.preview} id={image.id} className="galleryImages" />
                    <i
                      className="delPicOther"
                      onClick={() => removeImage(image.id)}
                    ></i>
                  </span>
                ))}
              </div>
            </div>

            <div className="mainAttributeModal">
              <h5 className="titles">مشخصات فنی</h5>
              <div className="modalAttributeGroup">
                {list()}
              </div>
            </div>
          </section>

          {/* Improved Shelf Products Management Section */}
          <section className="bottom width100">
            <div className="shelf-management-section">
              <div className="shelf-management-header">
                <button
                  type="button"
                  className="addShelfButton"
                  onClick={() => {
                    setForm(prev => ({
                      ...prev,
                      shelfProducts: [...(prev.shelfProducts || []), {
                        shelfId: shelves[0]?.id || 1,
                        count: 1,
                        unitType: 'box'
                      }]
                    }));
                  }}
                >
                  <span>+</span>
                  اضافه کردن قفسه
                </button>
                <h3 className="shelf-management-title">مدیریت قفسه ها</h3>
              </div>

              <div className="productTable2">
                <table className="editShelfTable">
                  <thead>
                  <tr className="dashTr33">
                    <th>عملیات</th>
                    <th>تعداد</th>
                    <th>نوع واحد</th>
                    <th>قفسه</th>
                    <th>ردیف</th>
                  </tr>
                  </thead>
                  <tbody>
                  {shelfProductsList()}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default ProductManage;